import { getApsAuthBase } from "../../auth/apsConfig";
import type { DmAdsRegion } from "./region";

export class DataManagementApiError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = "DataManagementApiError";
    this.status = status;
  }
}

/** Wrong item-id shape for this endpoint — try the next ACC id candidate. */
export function isRetryableAccItemPathError(err: unknown): boolean {
  if (!(err instanceof DataManagementApiError)) return false;
  if (err.status === 404) return true;
  if (err.status === 400) {
    return /Urn is not in the proper format|BAD_INPUT/i.test(err.message);
  }
  return false;
}

export interface DmFetchOptions {
  init?: RequestInit;
  /** Hub data residency — sets `x-ads-region` when US or EMEA. */
  region?: DmAdsRegion;
}

/**
 * Authenticated fetch against APS Data Management / Project APIs.
 * Uses the user's PKCE access token (3-legged).
 */
export async function dmFetch<T>(
  accessToken: string,
  path: string,
  options?: DmFetchOptions
): Promise<T> {
  const base = getApsAuthBase();
  const url = path.startsWith("http") ? path : `${base}${path}`;
  const headers: Record<string, string> = {
    Authorization: `Bearer ${accessToken}`,
    "Content-Type": "application/json",
    ...(options?.init?.headers as Record<string, string> | undefined),
  };
  if (options?.region) {
    headers["x-ads-region"] = options.region;
  }

  const res = await fetch(url, {
    ...options?.init,
    headers,
  });

  if (!res.ok) {
    const body = await res.text().catch(() => "");
    let hint = "";
    if (res.status === 401) {
      hint = " Session may have expired — sign out and sign in again.";
    } else if (res.status === 403) {
      hint =
        " The Forma Code Compliance APS app may not be provisioned under ACC Custom Integrations for this hub.";
    }
    throw new DataManagementApiError(
      res.status,
      `APS Data Management ${res.status}: ${body || res.statusText}.${hint}`
    );
  }

  return (await res.json()) as T;
}
